import { useCallback, useEffect, useState } from 'preact/hooks';
import { SERVICE_TOO_SLOW } from '../components/ui/resultWording.js';
import { api } from '../services/api.js';
import { type FetchState, classifyFetchError } from '../services/fetchState.js';

/**
 * 064: read from the platform, and report what actually happened.
 *
 * This used to return `{ data, loading, error }`. Eleven of its fourteen call sites destructured
 * `{ data, loading }`, so every failure rendered as the screen's own empty copy — an outage was
 * indistinguishable from an empty fleet. The state is now a discriminated union, which a screen
 * cannot silently drop.
 *
 * `path === null` means the screen has deliberately not asked yet (no id in the address, a panel
 * still closed). That is `idle`, not `loading` — a spinner that never resolves is its own lie.
 */
/**
 * 070: how long a single read may go unanswered before the wait is ended (H25, FR-002).
 *
 * Derived, not chosen by convention. Five staging endpoints (`/locations`, `/cars`, `/car-brands`,
 * `/bookings`, `/users/me/identity`) each answered in 0.11–0.13 s on 2026-08-07, so 15 s is ≈113×
 * the slowest observed read and cannot fire on a service that is merely slow. It also sits inside
 * `BookingReturnPage`'s own 20 s poll budget (`MAX_POLLS × POLL_INTERVAL_MS`), so a hung poll ends
 * that wait rather than outliving it.
 *
 * Exported so tests assert against the real value instead of restating a literal that can drift.
 */
export const READ_TIMEOUT_MS = 15_000;

/**
 * 085 (gap-list F6): `refreshKey` is a second reason to re-issue the same `path`.
 *
 * It exists because the notification surfaces mount **outside the `<Router>`**, so beginning or
 * stopping acting as another person changes who the app is acting as without unmounting them — and
 * they went on showing the previous identity's notifications for the whole session.
 *
 * 🔴 Compared by identity, like any dependency, so callers must pass a **primitive**. Passing an
 * object re-issues on every render; that is exactly the trap 051's `refreshUser()` sets, and the
 * reason `useActingIdentity()` returns an id rather than the user (research R7).
 *
 * Omitted by 16 of the 17 call sites, which are unaffected — the parameter is additive, and that
 * compatibility is the claim the whole approach rests on.
 *
 * Note what this deliberately is **not**: a polling interval. The trigger is a change of identity,
 * never the passage of time.
 */
export function useFetch<T>(
  path: string | null,
  refreshKey?: unknown,
): FetchState<T> & { retry: () => void } {
  const [state, setState] = useState<FetchState<T>>(
    path ? { status: 'loading', data: null } : { status: 'idle', data: null },
  );
  // Bumping this re-runs the effect against the same path, which is all a retry is.
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // `attempt` is deliberately a dependency the effect body never reads: bumping it is precisely
  // what re-issues the same path on retry. Removing it, as the rule suggests, makes retry a
  // no-op — the "retry re-issues the same path" test in tests/hooks/useFetch.test.tsx fails if
  // this dependency is dropped.
  //
  // 085: `refreshKey` is the same shape — never read in the body, and load-bearing purely as a
  // dependency. Dropping it lets a surface keep another identity's data (F6).
  // biome-ignore lint/correctness/useExhaustiveDependencies: see the note above
  useEffect(() => {
    if (!path) {
      setState({ status: 'idle', data: null });
      return;
    }

    // Guards an answer for a superseded path OR a superseded attempt: without the second case a
    // slow first retry could overwrite the result of a later one.
    let cancelled = false;
    // 070: this attempt has already produced its outcome (FR-009). Whichever of the answer, the
    // rejection or the expiry arrives first wins, and the others are discarded. Without it, the
    // abort the timer performs comes back as a rejection and overwrites the message it just set.
    let settled = false;
    setState({ status: 'loading', data: null });

    // 070: a read that neither answers nor fails must still end (H25, FR-001).
    //
    // The timer sets the state **itself** rather than aborting and letting the rejection classify
    // it, and that is the decision the whole feature turns on. An abort raises the same AbortError
    // whether it came from here or from the customer leaving, so classifying it would mean
    // inferring what cannot be inferred (FR-010) — and a customer who navigated away would be told
    // the service was slow. Deciding here, where the cause is known, removes the inference.
    const controller = new AbortController();
    const expiry = setTimeout(() => {
      if (cancelled || settled) return;
      settled = true;
      setState({ status: 'failed', data: null, reason: SERVICE_TOO_SLOW });
      // Stop the request too: the outcome is already decided, and a read nobody is waiting for
      // should not keep consuming a phone's data.
      controller.abort();
    }, READ_TIMEOUT_MS);

    api
      .get<T>(path, controller.signal)
      .then((data) => {
        if (cancelled || settled) return;
        settled = true;
        setState({ status: 'ok', data });
      })
      .catch((err: unknown) => {
        if (cancelled || settled) return;
        settled = true;
        const { status, reason } = classifyFetchError(err);
        setState({ status, data: null, reason });
      });

    return () => {
      cancelled = true;
      clearTimeout(expiry);
      // 070: stop the request, do not merely ignore its answer (FR-015). `cancelled` is set first,
      // so the rejection this raises is already suppressed by the time it arrives — a customer who
      // navigated away must never be told anything (FR-010).
      controller.abort();
    };
  }, [path, attempt, refreshKey]);

  return { ...state, retry };
}
